<template>
  <div
    class="vue-avatar--wrapper"
    :style="[style]"
    :title="username"
    :data-id="id"
  >
    <div :style="[style, initialBackgroundAndFontStyle, customStyle]">
      {{ userInitial }}
    </div>
    <div v-if="src" :style="[style, imgBackgroundAndFontStyle, customStyle]" />
  </div>
</template>

<script>
import { initials } from "../lib/initials.mjs";

export default {
  name: "Avatar",
  props: {
    id: {
      type: String,
      default: "",
      required: false,
    },
    username: {
      type: String,
      required: true,
    },
    initials: {
      type: String,
      default: "",
    },
    backgroundColor: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "",
    },
    customStyle: {
      type: Object,
      default: function () {
        return {};
      },
    },
    size: {
      type: String,
      default: "50",
    },
    src: {
      type: String,
      default: null,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const $this = this;
    return {
      sizeAsString: parseInt($this.size, 10),
      // Identity colours for initials avatars. Every entry is a deep tone from
      // a DESIGN.md colour family, and every one carries WHITE initials at
      // >= 5.36:1 (WCAG AA is 4.5) -- see fontColor below.
      //
      // What this replaces: the stock Material Design 2014 palette (#F44336
      // red ... #CDDC39 lime), which was off-brand -- none of those 18 colours
      // appear anywhere in DESIGN.md, and a wall of saturated primaries is the
      // "cheap / consumer-app" register PRODUCT.md lists as an anti-reference.
      // It was also unreadable: paired with the lightened font colour below,
      // ALL 18 failed WCAG AA, and all 18 failed even the relaxed 3:1
      // large-text bar. The lime/amber pairs measured 1.43:1 and 1.53:1.
      //
      // Coral is deliberately ABSENT. DESIGN.md's One Warm Light Rule reserves
      // it for action, selection and state; an avatar is an identity mark, not
      // an action, and spending coral here would dilute the one signal that
      // tells a user where to click.
      backgroundColors: [
        "#2D5264", // slate teal — DESIGN.md secondary          8.39:1
        "#286796", // info blue — DESIGN.md semantic            6.05:1
        "#4F7263", // sage mint, deepened for white text        5.36:1
        "#8A5A2B", // warm gold, deepened for white text        5.87:1
        "#6B4A5B", // plum, warm neutral-dark                   7.62:1
        "#4A4C63", // ink muted, deepened                       8.38:1
        "#476E24", // success green, deepened                   5.96:1
        "#6B5CA5", // chart-3 purple family                     5.68:1
        "#0A6E75", // deep teal, chart-1 family                 6.00:1
      ],
    };
  },
  computed: {
    background() {
      if (this.username.length == 0) {
        return "#ffffff";
      } else {
        return (
          this.backgroundColor ||
          this.randomBackgroundColor(this.username, this.backgroundColors)
        );
      }
    },
    fontColor() {
      // White, not a lightened tint of the background. lightenColor(bg, 80)
      // produced pale-on-saturated pairs that failed AA on every one of the
      // old palette's 18 colours (worst: #FFFF89 on #CDDC39, 1.43:1). Every
      // colour in backgroundColors above is chosen so white clears 4.5:1.
      //
      // This leaves lightenColor() and the `lighten` prop with no callers, so
      // both are removed below rather than left as dead code.
      return this.color || "#FFFFFF";
    },
    isImage() {
      return Boolean(this.src);
    },
    initialBackgroundAndFontStyle() {
      return {
        zposition: "absolute",
        ztop: 0,
        backgroundColor: this.background,
        font: `${Math.floor(
          this.sizeAsString / 2.5
        )}px/100px Helvetica, Arial, sans-serif`,
        fontWeight: "bold",
        color: this.fontColor,
        lineHeight: `${
          this.sizeAsString + Math.floor(this.sizeAsString / 20)
        }px`,
      };
    },
    imgBackgroundAndFontStyle() {
      let styleObject = {
        position: "absolute",
        backgroundSize: "cover",
        top: 0,
      };

      if (this.src && this.src != "null") {
        styleObject.background = `transparent url(${this.src}) no-repeat scroll 50% 50% / cover content-box border-box `;
        // ${this.sizeAsString}px ${this.sizeAsString}px
      }

      return styleObject;
    },
    style() {
      return {
        width: `${this.sizeAsString}px`,
        height: `${this.sizeAsString}px`,
        borderRadius: this.rounded ? "50%" : 0,
        textAlign: "center",
        verticalAlign: "middle",
      };
    },
    userInitial() {
      const initials = this.initials || this.initialsFromUserName;
      return initials;
    },
    initialsFromUserName() {
      return initials(this.username);
    },
  },
  mounted() {
    this.$emit("avatar-initials", this.username, this.userInitial);
  },
  methods: {
    // Hash the whole username, not its length. The previous seed was
    // `username.length`, so every name of the same length got the same colour
    // -- "Leila Hassan" and "Priya Kapoor" are both 12 characters. That was
    // already weak against 18 colours; this palette has 9, which would have
    // doubled the collision rate. A cheap deterministic string hash spreads
    // names across the palette instead, and stays stable for a given user
    // (the same name always yields the same colour, which is the whole point
    // of an identity mark).
    randomBackgroundColor(username, colors) {
      let hash = 0;
      for (let i = 0; i < username.length; i += 1) {
        hash = (hash * 31 + username.charCodeAt(i)) % 1000000007;
      }
      return colors[hash % colors.length];
    },
  },
};
</script>

<style scoped lang="scss">
// .online {
//   animation: online-pulse 2s infinite;
// }

.vue-avatar--wrapper.online {
  position: relative;
  &:after {
    content: "";
    width: 25%;
    height: 25%;
    border-radius: 100%;
    background-color: green;
    border: 2px solid #fff;
    display: block;
    position: absolute;
    bottom: 10%;
    right: 0px;
  }
}

// .away {
//   box-shadow: 0px 0px 0px 0.2em grey;
// }

@keyframes online-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(49, 102, 0, 1);
  }

  70% {
    box-shadow: 0 0 0 0.5em rgba(0, 0, 0, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}
</style>
